import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/Card';
import { Spinner } from '@/components/ui/Spinner';
import { PnlDisplay } from '@/components/ui/PnlDisplay';
import { useChainHistory } from '@/hooks/queries/useChains';
import { formatDateTime, formatCurrency, formatOptionSymbol } from '@/lib/formatters';
import { cn } from '@/lib/utils';

interface ChainTimelineProps {
  chainId: string;
}

export function ChainTimeline({ chainId }: ChainTimelineProps) {
  const { data, isLoading } = useChainHistory(chainId);

  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Timeline</CardTitle>
        </CardHeader>
        <CardContent className="flex items-center justify-center py-8">
          <Spinner />
        </CardContent>
      </Card>
    );
  }

  if (!data) return null;

  const { timeline } = data;

  const getEventIcon = (type: string) => {
    switch (type) {
      case 'opened':
        return (
          <div className="h-8 w-8 rounded-full bg-green-100 flex items-center justify-center">
            <svg className="h-4 w-4 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
          </div>
        );
      case 'rolled':
        return (
          <div className="h-8 w-8 rounded-full bg-blue-100 flex items-center justify-center">
            <svg className="h-4 w-4 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
              />
            </svg>
          </div>
        );
      case 'closed':
        return (
          <div className="h-8 w-8 rounded-full bg-gray-100 flex items-center justify-center">
            <svg className="h-4 w-4 text-gray-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
          </div>
        );
      default:
        return null;
    }
  };

  const getEventTitle = (type: string, position: typeof timeline[0]['position']) => {
    switch (type) {
      case 'opened':
        return 'Position Opened';
      case 'rolled':
        return `Rolled to Position #${position.rollNumber}`;
      case 'closed':
        // Check close reasons to provide better title
        const closeReasons = new Set(position.legs.map((l: any) => l.closeReason).filter(Boolean));
        if (closeReasons.has('assigned') || closeReasons.has('exercised')) {
          if (closeReasons.has('expired')) {
            return 'Expiration (Mixed)';
          }
          return 'Assigned/Exercised';
        }
        if (closeReasons.has('expired')) {
          return 'Expired';
        }
        return 'Position Closed';
      default:
        return type;
    }
  };

  // Position P&L is net premium only — stock P&L is tracked separately via stock_positions
  const calculateClosePnl = (position: typeof timeline[0]['position']) => {
    return position.netPremium;
  };

  // Get close reason badge styling
  const getCloseReasonStyle = (reason: string) => {
    switch (reason) {
      case 'expired': return 'bg-gray-100 text-gray-600';
      case 'assigned': return 'bg-amber-100 text-amber-700';
      case 'exercised': return 'bg-purple-100 text-purple-700';
      default: return 'bg-green-100 text-green-700';
    }
  };

  const formatCloseReason = (reason: string) => {
    switch (reason) {
      case 'expired': return 'Expired';
      case 'assigned': return 'Assigned';
      case 'exercised': return 'Exercised';
      default: return 'Closed';
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>Timeline</CardTitle>
      </CardHeader>
      <CardContent>
        <div className="relative">
          {/* Timeline line */}
          <div className="absolute left-4 top-0 bottom-0 w-px bg-gray-200" />

          {/* Events */}
          <div className="space-y-6">
            {timeline.map((event, index) => (
              <div key={`${event.type}-${event.date}-${index}`} className="relative flex gap-4">
                {getEventIcon(event.type)}
                <div className="flex-1 pt-1">
                  <div className="flex items-center justify-between">
                    <h4 className="font-medium text-gray-900">
                      {getEventTitle(event.type, event.position)}
                    </h4>
                    <span className="text-sm text-gray-500">
                      {formatDateTime(event.date)}
                    </span>
                  </div>
                  {event.position && (
                    <div className="mt-2 text-sm">
                      {event.type !== 'closed' && (
                        <div className="space-y-1">
                          {/* Show all legs sorted by strike, if 4 or fewer; otherwise show 3 + "+N more" */}
                          {[...event.position.legs].sort((a: any, b: any) => a.strikePrice - b.strikePrice).slice(0, event.position.legs.length <= 4 ? 4 : 3).map((leg) => (
                            <div key={leg.id} className="text-gray-600">
                              {leg.action} {leg.quantity}x {formatOptionSymbol(leg.optionSymbol)} @ {formatCurrency(leg.pricePerContract)}
                            </div>
                          ))}
                          {event.position.legs.length > 4 && (
                            <div className="text-gray-400">
                              +{event.position.legs.length - 3} more legs
                            </div>
                          )}
                        </div>
                      )}
                      {event.type === 'closed' && (
                        <div className="space-y-2">
                          {/* Show leg outcomes sorted by strike price */}
                          <div className="space-y-1">
                            {[...event.position.legs].sort((a: any, b: any) => a.strikePrice - b.strikePrice).map((leg: any) => (
                              <div key={leg.id} className="flex items-center gap-2 text-gray-600">
                                <span className={cn(
                                  'text-xs px-1.5 py-0.5 rounded font-medium',
                                  leg.optionType === 'call' ? 'bg-blue-100 text-blue-700' : 'bg-pink-100 text-pink-700'
                                )}>
                                  {leg.optionType === 'call' ? 'C' : 'P'}
                                </span>
                                <span>${leg.strikePrice}</span>
                                {leg.closeReason && (
                                  <span className={cn(
                                    'text-xs px-1.5 py-0.5 rounded',
                                    getCloseReasonStyle(leg.closeReason)
                                  )}>
                                    {formatCloseReason(leg.closeReason)}
                                  </span>
                                )}
                              </div>
                            ))}
                          </div>
                          {/* Show P&L */}
                          <div className="flex items-center gap-2 pt-1 border-t border-gray-100">
                            <span className="text-gray-600">P&L:</span>
                            <PnlDisplay value={calculateClosePnl(event.position)} size="sm" />
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
